import { Link } from "react-router-dom";
import { MapPin, ArrowRight, User, Phone, Mail, Building2, Calendar, Package } from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const TONE = {
  Delivered: "emerald", Completed: "emerald", "Active Contract": "emerald",
  Cancelled: "red",
  Confirmed: "sky", "Out for Delivery": "sky", "Expert Assigned": "sky",
};

export function StatusBadge({ label, testid }) {
  const tone = TONE[label] || "primary";
  const cls = tone === "emerald" ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
    : tone === "red" ? "border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400"
      : tone === "sky" ? "border-sky-500/50 bg-sky-500/10 text-sky-600 dark:text-sky-400"
        : "border-primary/50 bg-primary/10 text-primary";
  return <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide whitespace-nowrap ${cls}`} data-testid={testid}><span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />{label}</span>;
}

function CardHead({ id, date, status, testid }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="font-mono-num font-bold text-primary truncate" data-testid={`${testid}-id`}>{id}</div>
        <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5"><Calendar className="h-3 w-3" /> {fmtDate(date)}</div>
      </div>
      <StatusBadge label={status} testid={`${testid}-status`} />
    </div>
  );
}

function Meta({ label, value, strong }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-sm truncate ${strong ? "font-mono-num font-bold text-primary" : "font-medium"}`}>{value || "—"}</div>
    </div>
  );
}

function Contact({ contact, title }) {
  if (!contact?.name) return <div className="text-xs text-muted-foreground border-t border-border pt-3">{title}: will be assigned soon</div>;
  return (
    <div className="border-t border-border pt-3 text-xs space-y-1">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{title}</div>
      <div className="flex items-center gap-1.5 font-medium text-sm"><User className="h-3.5 w-3.5 text-primary" /> {contact.name}</div>
      {contact.phone && <a href={`tel:${contact.phone}`} className="flex items-center gap-1.5 hover:text-primary"><Phone className="h-3.5 w-3.5" /> {contact.phone}</a>}
      {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 hover:text-primary truncate"><Mail className="h-3.5 w-3.5" /> {contact.email}</a>}
    </div>
  );
}

export function PickupOrderCard({ o }) {
  const t = `pd-order-${o.booking_id}`;
  return (
    <article className="border border-border bg-card p-5 space-y-4" data-testid={t}>
      <CardHead id={o.booking_id} date={o.created_at} status={o.status} testid={t} />
      <div className="text-sm space-y-1.5">
        <div className="flex gap-2"><MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span><span className="text-muted-foreground text-xs uppercase tracking-wide">Pickup · </span>{o.pickup || "—"}</span></div>
        <div className="flex gap-2"><ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span><span className="text-muted-foreground text-xs uppercase tracking-wide">Delivery · </span>{o.delivery || "—"}</span></div>
      </div>
      {(o.categories.length > 0 || o.items.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {o.items.length > 0 ? o.items.map((it, i) => (
            <span key={i} className="border border-border bg-muted px-2 py-0.5 text-xs"><Package className="inline h-3 w-3 mr-1 text-primary" />{it.name}{it.articles ? ` × ${it.articles}` : ""}</span>
          )) : o.categories.map((c) => <span key={c} className="border border-border bg-muted px-2 py-0.5 text-xs">{c}</span>)}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 border-t border-border pt-3">
        <Meta label="Delivery Type" value={`${o.delivery_type.charAt(0).toUpperCase()}${o.delivery_type.slice(1)} (${o.delivery_multiplier}×)`} />
        <Meta label="Volumetric Wt" value={`${Number(o.volumetric_weight || 0).toFixed(2)} kg`} />
        <Meta label="Chargeable Wt" value={`${Number(o.chargeable_weight || 0).toFixed(2)} kg`} />
        <Meta label="Estimated Cost" value={inr(o.cost)} strong />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className={`text-[11px] font-bold uppercase tracking-wide ${o.payment_status === "paid" ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600"}`} data-testid={`${t}-payment`}>Payment: {o.payment_status}</span>
        {o.track_token && <Link to={`/pickup-track/${o.track_token}`} className="text-xs font-bold uppercase tracking-wide text-primary hover:underline inline-flex items-center gap-1" data-testid={`${t}-track`}>Track order <ArrowRight className="h-3.5 w-3.5" /></Link>}
      </div>
    </article>
  );
}

export function PackersOrderCard({ o }) {
  const t = `pm-order-${o.lead_id}`;
  return (
    <article className="border border-border bg-card p-5 space-y-4" data-testid={t}>
      <CardHead id={o.lead_id} date={o.created_at} status={o.status} testid={t} />
      <div className="grid grid-cols-2 gap-3">
        <Meta label="Service Type" value={o.service_type} />
        <Meta label="Route" value={o.route} />
      </div>
      <Contact contact={o.contact} title="Assigned team contact" />
    </article>
  );
}

export function ThreePLCard({ o }) {
  const t = `tpl-enquiry-${o.id}`;
  return (
    <article className="border border-border bg-card p-5 space-y-4" data-testid={t}>
      <CardHead id={o.enquiry_id} date={o.created_at} status={o.status} testid={t} />
      <div className="grid grid-cols-2 gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Company</div>
          <div className="text-sm font-medium truncate flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-primary" />{o.company_name || "—"}</div>
        </div>
        <Meta label="Business Model" value={o.business_model} />
      </div>
      <Contact contact={o.contact} title="Fulfillment expert" />
    </article>
  );
}
